// Chatbot status copy shared by the chat header dot, the in-thread handoff
// marker and the conversation-list badge. One glyph, three colours, one meaning
// per colour: green answers, yellow is resting, red has handed the conversation
// to a person. The backend records WHY the bot is quiet in
// Twilio_Conversation.chatbot_pause_meta (reason / by_name / at), exposed as
// chatbot_pause_reason, chatbot_paused_by_name, chatbot_paused_at.

// source_platform stamped on the web-widget holding message ("I've passed your
// question along to our team") -- the bot's own handoff.
export const CHATBOT_HANDOFF_SOURCE_PLATFORM = 'AI AUTO-REPLY FALLBACK';

export const PAUSE_REASON = {
  AGENT_REPLY: 'agent_reply',
  HANDOFF: 'handoff',
  IDENTIFY: 'identify',
  MANUAL: 'manual',
};

export function isHandoffMessage(message) {
  const sourcePlatform = message?.attributes?.source_platform;
  return typeof sourcePlatform === 'string'
    && sourcePlatform.toUpperCase().includes(CHATBOT_HANDOFF_SOURCE_PLATFORM);
}

function formatWhen(value) {
  const d = value instanceof Date ? value : (value ? new Date(value) : null);
  if (!d || isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const today = new Date();
  const sameDay = d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  return sameDay ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}

// The second, quieter tooltip line: who or what caused the pause. Null when the
// backend recorded nothing (rows paused before chatbot_pause_meta existed).
export function describePauseCause({ reason, byName, at }) {
  const when = formatWhen(at);
  let cause = null;
  switch (reason) {
    case PAUSE_REASON.HANDOFF:
      cause = "It couldn't answer confidently";
      break;
    case PAUSE_REASON.IDENTIFY:
      cause = 'The visitor asked for a person';
      break;
    case PAUSE_REASON.AGENT_REPLY:
      cause = byName ? `${byName} replied` : 'A team member replied';
      break;
    case PAUSE_REASON.MANUAL:
      cause = byName ? `By ${byName}` : 'From the chatbot panel';
      break;
    default:
      return null;
  }
  return when ? `${cause} · ${when}` : cause;
}

// Headline for the status dot's tooltip.
//   active            -> "Chatbot is answering"
//   timed pause       -> "Chatbot paused until 13:34"
//   stopped, handoff  -> "Chatbot handed off to your team"
//   stopped, other    -> "Chatbot stopped"
export function chatbotStatusHeadline({ stopped, paused, pausedUntil, reason }) {
  if (stopped) {
    return (reason === PAUSE_REASON.HANDOFF || reason === PAUSE_REASON.IDENTIFY)
      ? 'Chatbot handed off to your team'
      : 'Chatbot stopped';
  }
  if (paused) {
    const until = formatWhen(pausedUntil);
    return until ? `Chatbot paused until ${until}` : 'Chatbot paused';
  }
  return 'Chatbot is answering';
}

// Sentence for the in-thread handoff marker while the bot is still quiet.
export function chatbotQuietSentence({ stopped, paused, pausedUntil }) {
  if (stopped) return 'The chatbot is stopped for this conversation.';
  if (paused) {
    const until = formatWhen(pausedUntil);
    return until ? `The chatbot is paused until ${until}.` : 'The chatbot is paused for this conversation.';
  }
  return null;
}
